import React, { useEffect, useState } from 'react';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';
import Pagination from '../components/Pagination';
import Drawer from '../../sharedComponents/Drawer';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';
import { Eye, MoreVertical, CheckCircle, Clock } from 'lucide-react';
import { useNotification } from '../../sharedComponents/Notifications';
import {
    submissionService,
    categoryService,
    Submission,
    Category,
    SubmissionStatus,
    WorkflowStage,
    WORKFLOW_STAGE_LABELS,
    GetAllAdminSubmissionsPayload,
} from '../services/adminAuth';
import { formatDateDisplay } from '../../../utils/dateHelper';

const STATUS_FILTER_OPTIONS: { value: number; label: string }[] = [
    { value: SubmissionStatus.Submitted, label: 'Submitted' },
    { value: SubmissionStatus.Approved,  label: 'Approved'  },
    { value: SubmissionStatus.Rejected,  label: 'Rejected'  },
];

const STAGE_FILTER_OPTIONS: { value: number; label: string }[] = Object
    .entries(WORKFLOW_STAGE_LABELS)
    .map(([value, label]) => ({ value: Number(value), label }));

const formatApplicationType = (t?: number) =>
    t === 1 ? 'Book' : t === 2 ? 'Journal' : '—';

// Same formatter used in ReviewerEvaluationReports for the final decision strings.
const formatFinalDecisionLabel = (d?: string | null) => {
    switch (d) {
        case 'AcceptableForPublication':     return 'Acceptable for Publication';
        case 'NotAcceptableForPublication':  return 'Not Acceptable for Publication';
        case 'AcceptableForFunding':         return 'Acceptable for Funding';
        case 'NotAcceptableForFunding':      return 'Not Acceptable for Funding';
        case 'ReturnToReviewer':             return 'Return to Reviewer for Re-Assessment';
        case 'ReturnToAuthorForRevisedCopy': return 'Return to Author for Revised Copy';
        default:                             return d ?? null;
    }
};

const formatPrimaFacieDecision = (d?: string | null) => {
    if (!d) return null;
    if (d === 'Recommended')    return 'Recommended';
    if (d === 'NotRecommended') return 'Not Recommended';
    return d;
};

const decisionBadgeClass = (d?: string | null) => {
    if (d === 'NotRecommended' || d === 'NotAcceptableForPublication' || d === 'NotAcceptableForFunding')
        return 'bg-red-50 text-red-700';
    if (d === 'ReturnToReviewer' || d === 'ReturnToAuthorForRevisedCopy')
        return 'bg-amber-50 text-amber-700';
    return 'bg-emerald-50 text-emerald-700';
};

const statusBadgeClasses = (status?: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('approved'))            return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (s.includes('reject'))              return 'bg-red-50 text-red-700 border-red-200';
    if (s.includes('return'))              return 'bg-amber-50 text-amber-700 border-amber-200';
    if (s.includes('review'))              return 'bg-blue-50 text-blue-700 border-blue-200';
    if (s.includes('submit'))              return 'bg-slate-50 text-slate-700 border-slate-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
};

const AllSubmissions: React.FC = () => {
    const { showNotification } = useNotification();

    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Filters — 0 means "All" for every filter. Backend treats 0 as no filter.
    const [filterStatus, setFilterStatus] = useState<number>(0);
    const [filterStage, setFilterStage] = useState<number>(0);
    const [filterCategory, setFilterCategory] = useState<number>(0);
    const [categories, setCategories] = useState<Category[]>([]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // View Details Drawer
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);

    // ── Fetchers ─────────────────────────────────────────────────────────────

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await categoryService.getAllCategories(true);
                if (!cancelled && res.isSuccess && res.data) setCategories(res.data);
            } catch { /* fail soft */ }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const handler = setTimeout(async () => {
            setLoading(true);
            try {
                const payload: GetAllAdminSubmissionsPayload = {
                    pageNumber: currentPage,
                    pageSize,
                    sortBy: '',
                    sortDescending: true,
                    search,
                    categoryId: filterCategory,
                    submissionStatus: filterStatus,
                    currentStage: filterStage,
                };
                const res = await submissionService.getAllAdminSubmissions(payload);
                if (cancelled) return;
                if (res.isSuccess && res.data) {
                    setSubmissions(res.data.data);
                    setTotalRecords(res.data.totalRecords);
                    setTotalPages(res.data.totalPages);
                } else {
                    showNotification('error', {
                        title: 'Error',
                        message: res.message || 'Failed to load submissions',
                    });
                }
            } catch {
                if (cancelled) return;
                showNotification('error', {
                    title: 'Error',
                    message: 'An error occurred while loading submissions',
                });
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 350);

        return () => {
            cancelled = true;
            clearTimeout(handler);
        };
        // showNotification is intentionally omitted — it isn't referentially
        // stable from useNotification(), and including it would re-run this
        // effect every render in a loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, search, filterCategory, filterStatus, filterStage]);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleSearch = (q: string) => {
        setSearch(q);
        setCurrentPage(1);
    };

    const handleViewDetails = (row: Submission) => {
        setSelectedSubmission(row);
        setIsDetailsOpen(true);
    };

    const handleClearFilters = () => {
        setFilterStatus(0);
        setFilterStage(0);
        setFilterCategory(0);
        setCurrentPage(1);
    };

    // ── Columns ──────────────────────────────────────────────────────────────

    const columns: TableColumn<Submission>[] = [
        {
            key: 'submissionNumber',
            label: 'Submission ID',
            render: (value) => <span className="font-bold text-gray-900">{value}</span>,
        },
        {
            key: 'title',
            label: 'Title',
            className: 'max-w-[260px]',
            render: (value: string) => (
                <div className="font-medium text-gray-700 leading-snug truncate" title={value}>
                    {value}
                </div>
            ),
        },
        {
            key: 'author',
            label: 'Author',
            sortValue: (row) => row.author?.person?.fullName ?? '',
            render: (_, row) => (
                <span className="text-sm text-gray-700">
                    {row.author?.person?.fullName ?? '—'}
                </span>
            ),
        },
        {
            key: 'applicationType',
            label: 'Type',
            render: (_, row) => (
                <span className="text-sm text-gray-600">{formatApplicationType(row.applicationType)}</span>
            ),
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (_, row) => (
                <span className="text-sm text-gray-600">{row.categoryName ?? row.category?.name ?? '—'}</span>
            ),
        },
        {
            key: 'currentStage',
            label: 'Desk',
            render: (value: number) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {WORKFLOW_STAGE_LABELS[value] ?? '—'}
                </span>
            ),
        },
        {
            key: 'completedPrimaFacie',
            label: 'Completed Prima Facie /\nPrima Facie Decision',
            className: '!whitespace-pre-line align-top max-w-[160px]',
            sortable: false,
            render: (_, row) => {
                const done = row.completedPrimaFacie === true;
                const decLabel = formatPrimaFacieDecision(row.primaFacieDecision);
                return (
                    <div className="flex flex-col gap-1.5 items-start">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                            done ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                            {done ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                            {done ? 'Yes' : 'No'}
                        </span>
                        {decLabel ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${decisionBadgeClass(row.primaFacieDecision)}`}>
                                {decLabel}
                            </span>
                        ) : (
                            <span className="text-xs text-gray-400">—</span>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'finalDecision',
            label: 'Final Decision',
            sortable: false,
            render: (_, row) => {
                const decLabel = formatFinalDecisionLabel(row.finalDecision);
                if (!decLabel) return <span className="text-xs text-gray-400">—</span>;
                return (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${decisionBadgeClass(row.finalDecision)}`}>
                        {decLabel}
                    </span>
                );
            },
        },
        {
            key: 'status',
            label: 'Status',
            render: (value: string) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold border ${statusBadgeClasses(value)}`}>
                    {value || '—'}
                </span>
            ),
        },
        {
            key: 'submittedAt',
            label: 'Submitted',
            render: (value: string) => (
                <span className="text-xs text-gray-500">{formatDateDisplay(value)}</span>
            ),
        },
    ];

    const actions: TableAction<Submission>[] = [
        {
            icon: <MoreVertical className="w-5 h-5" />,
            onClick: () => undefined,
            title: 'Actions',
            variant: 'dropdown',
            dropdownItems: [
                {
                    label: 'View Details',
                    icon: <Eye className="w-4 h-4 text-blue-600" />,
                    onClick: handleViewDetails,
                    className: 'text-blue-600',
                },
            ],
        },
    ];

    // ── Filter Panel ─────────────────────────────────────────────────────────

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                    <select
                        value={filterStatus}
                        onChange={(e) => { setFilterStatus(Number(e.target.value)); setCurrentPage(1); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#088A61]"
                    >
                        <option value={0}>All statuses</option>
                        {STATUS_FILTER_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Desk</label>
                    <select
                        value={filterStage}
                        onChange={(e) => { setFilterStage(Number(e.target.value)); setCurrentPage(1); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#088A61]"
                    >
                        <option value={0}>All desks</option>
                        {STAGE_FILTER_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Thematic Area</label>
                    <select
                        value={filterCategory}
                        onChange={(e) => { setFilterCategory(Number(e.target.value)); setCurrentPage(1); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#088A61]"
                    >
                        <option value={0}>All thematic areas</option>
                        {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <button
                onClick={handleClearFilters}
                className="mt-4 text-sm text-[#088A61] hover:underline font-medium"
            >
                Clear all filters
            </button>
        </div>
    );

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">All Submissions</h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Cross-cutting view of every submission in the system. Filter by status, desk, or thematic area.
                </p>
            </div>

            <ReusableTable
                title="Submissions"
                subTitle={`${totalRecords} record${totalRecords === 1 ? '' : 's'}`}
                columns={columns}
                data={submissions}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, ID, author…"
                onSearch={handleSearch}
                emptyMessage="No submissions found."
                showSearch
                showFilter
                filterPanel={filterPanel}
                isFilterOpen={isFilterOpen}
                onFilterToggle={() => setIsFilterOpen(o => !o)}
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            <Drawer
                isOpen={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                title="Submission Details"
                width="max-w-3xl"
            >
                {selectedSubmission && (
                    <AdminSubmissionDetails submissionId={selectedSubmission.id} />
                )}
            </Drawer>
        </div>
    );
};

export default AllSubmissions;
